<!-- @format -->

<template>
	<div class="flex flex-col">
		<CoreTwoColumnsRow class="mb-3">
			<template #left>
				<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-55">
					{{ $t('§contracts.depreciation_period') }}
				</CoreBorderWrapper>
			</template>
			<template #right>
				<CoreDropdown
					class="flex-grow-1 flex-shrink-1 flex-basis-45"
					name="rvtDepreciationPeriodYears"
					v-model="selectedPeriod"
					:options="depreciationPeriodOptions"
					:sort="false"
					:disabled="isRecalculationDisabled"
					@update:modelValue="onDepreciationPeriodChange"
				/>
			</template>
		</CoreTwoColumnsRow>

		<div
			v-if="unitTypeStore.rvtPendingRecalculation !== null"
			class="bg-warning-light mb-6 px-10 py-3 rounded flex flex-col justify-start gap-2"
		>
			<span>{{ $t('§eq_type.recalculate_replaces_rv_table_values') }}</span>
			<div class="flex gap-2">
				<CoreButton
					label="§ui_button.proceed"
					name="confirmRvtRecalculation"
					variant="primary"
					icon="pi-refresh"
					@click="confirmRecalculation"
				/>
				<CoreButton
					label="§ui_button.keep_current_values"
					name="cancelRvtRecalculation"
					variant="tertiary"
					icon="pi-times"
					@click="cancelRecalculation"
				/>
			</div>
		</div>

		<div class="flex flex-start justify-start">
			<DataTable
				:value="table"
				editMode="cell"
				:loading="unitTypeStore.calculating"
				@cell-edit-init="onCellEditInit"
				@cell-edit-complete="onCellEditComplete"
				@cell-edit-cancel="onCellEditCancel"
				class="residual-values-table rv-matrix rv-matrix--single"
				:class="{
					'filter-blur': unitTypeStore.calculating,
				}"
				responsiveLayout="scroll"
			>
				<Column
					field="months"
					headerClass="rv-mh"
					bodyClass="rv-mcol"
				>
					<template #header>
						<span class="font-bold">
							{{ $t('§eq_type.months') }}
						</span>
					</template>
					<template #body="slotProps">
						<span>
							{{
								slotProps.data.months === 1
									? $t('§eq_type.day_1')
									: slotProps.data.months
							}}
						</span>
					</template>
				</Column>

				<template
					v-for="key in displayedUtilizationKeys"
					:key="key"
				>
					<Column
						:field="key"
						:pt="{
							headerCell: { class: 'rv-gh', 'data-g': 0 },
							bodyCell: { class: 'rv-gcol', 'data-g': 0 },
						}"
					>
						<template #header>
							<span class="rv-gh__label">
								<span class="pctlab font-bold">{{ key }}</span>
								<span class="util">{{ $t('§contracts.utilisation') }}</span>
							</span>
						</template>
						<template #body="slotProps">
							<CoreCurrencyPercentageInput
								:modelValue="slotProps.data[key]"
								:baseValue="baseValue"
								:currency="currencyCode"
								:currencyDigits="RV_CURRENCY_DIGITS"
								:percentageDigits="1"
								displayOnly
							/>
						</template>
						<template #editor="slotProps">
							<CoreCurrencyPercentageInput
								v-model="slotProps.data[key]"
								class="modal-residual-values-table__editor"
								:baseValue="baseValue"
								:currency="currencyCode"
								:currencyDigits="RV_CURRENCY_DIGITS"
								:percentageDigits="1"
								:showBorder="false"
								autofocus
								:autofocusSide="editFocusSide"
								:validator="getCellValidator(slotProps.data.months, key)"
								:customNotificationMessage="
									getCellValidationMessage(slotProps.data.months, key)
								"
							/>
						</template>
					</Column>
				</template>
			</DataTable>
		</div>

		<div class="flex mt-3">
			<CoreButton
				label="§ui_button.recalculate_rvt"
				name="recalculateRvt"
				variant="secondary"
				icon="pi-refresh"
				:disabled="isRecalculationDisabled"
				:loading="unitTypeStore.calculating"
				@click="onRecalculateClick"
			/>
		</div>
	</div>
</template>

<script>
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { useUnitTypeStore } from '@/stores/unitType'
import { rvDepreciationPeriodOptions } from '@/helpers/rvDepreciationPeriodOptions.js'
import { roundHalfUp } from '@/helpers/roundHalfUp.js'
import { RV_CURRENCY_DIGITS } from '@/data/constants.js'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreCurrencyPercentageInput from '@/components/core/CoreCurrencyPercentageInput.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'

export default {
	name: 'ResidualValuesTable',

	components: {
		DataTable,
		Column,
		CoreBorderWrapper,
		CoreButton,
		CoreCurrencyPercentageInput,
		CoreDropdown,
		CoreTwoColumnsRow,
	},

	data() {
		return {
			unitTypeStore: useUnitTypeStore(),
			table: [],
			cellValidationErrors: {},
			editFocusSide: null,
			selectedPeriod: null,
			lastAppliedPeriod: null,
			isUnmounted: false,
			RV_CURRENCY_DIGITS: RV_CURRENCY_DIGITS,
		}
	},

	computed: {
		currencyCode() {
			return this.unitTypeStore.unitTypeData.currency_code || 'EUR'
		},

		displayedUtilizationKeys() {
			return ['100%']
		},

		value() {
			return this.unitTypeStore.unitTypeData.value
		},

		baseValue() {
			return Number(this.value) || 0
		},

		isRecalculationDisabled() {
			return this.unitTypeStore.calculating || this.unitTypeStore.saving || this.baseValue < 1
		},

		depreciationPeriodOptions() {
			return rvDepreciationPeriodOptions(this.$t)
		},
	},

	watch: {
		table: {
			handler(newValue) {
				const rvt = {}

				newValue.forEach((row) => {
					const { months, ...utilizationColumns } = row
					Object.keys(utilizationColumns).forEach((key) => {
						const numberKey = Number(key.slice(0, -1)) / 100

						const laravelFriendlyKey = numberKey.toString().replace('.', '-')

						if (rvt[laravelFriendlyKey] === undefined) {
							rvt[laravelFriendlyKey] = {}
						}
						rvt[laravelFriendlyKey][months] = utilizationColumns[key]
					})
				})

				this.unitTypeStore.unitTypeData.residual_values_table = rvt
			},
			deep: true,
		},

		'unitTypeStore.rvtRevision'() {
			this.buildTable()
		},
	},

	mounted() {
		this.getRvt()
	},

	beforeUnmount() {
		this.isUnmounted = true
		// the prompt is in the store only so the Save buttons see it; it ends with this view
		this.unitTypeStore.rvtPendingRecalculation = null
	},

	methods: {
		onCellEditInit(event) {
			const target = event.originalEvent?.target
			this.editFocusSide = target?.closest?.('.core-currency-percentage-input__percentage')
				? 'percentage'
				: 'currency'
		},

		getCellValidationMessage(months, field) {
			const key = `${months}-${field}`
			return this.cellValidationErrors[key] || null
		},

		getCellValidator(months, field) {
			const key = `${months}-${field}`
			const hasError = this.cellValidationErrors[key] !== undefined

			return {
				$dirty: hasError,
				$error: hasError,
				$invalid: hasError,
				$touch: () => {},
				$reset: () => {},
			}
		},

		getRvt() {
			this.selectedPeriod = this.unitTypeStore.unitTypeData.rv_depreciation_period_years
			this.lastAppliedPeriod = this.selectedPeriod
			this.buildTable()
		},

		buildTable() {
			this.table = this.getTableData(
				this.filterToFullUtilization(this.unitTypeStore.unitTypeData.residual_values_table || {}),
			)
			this.cellValidationErrors = {}
		},

		onDepreciationPeriodChange(newValue) {
			const period = Number(newValue)
			if (period === this.lastAppliedPeriod) {
				this.unitTypeStore.rvtPendingRecalculation = null
				return
			}
			return this.requestRecalculation(period)
		},

		onRecalculateClick() {
			this.selectedPeriod = this.lastAppliedPeriod
			return this.requestRecalculation(this.lastAppliedPeriod)
		},

		requestRecalculation(period) {
			if (this.unitTypeStore.rvtEditedSinceBaseline) {
				this.unitTypeStore.rvtPendingRecalculation = { period }
				return
			}
			return this.recalculate(period)
		},

		confirmRecalculation() {
			const { period } = this.unitTypeStore.rvtPendingRecalculation
			this.unitTypeStore.rvtPendingRecalculation = null
			return this.recalculate(period)
		},

		cancelRecalculation() {
			this.unitTypeStore.rvtPendingRecalculation = null
			this.selectedPeriod = this.lastAppliedPeriod
		},

		async recalculate(period) {
			const rvt = await this.unitTypeStore.recalculateRvtFromTable({ period })
			if (this.isUnmounted) {
				return
			}
			if (!rvt) {
				this.selectedPeriod = this.lastAppliedPeriod
				this.unitTypeStore.notification('error', '§ui_message.update_failed')
				return
			}
			this.lastAppliedPeriod = period
			this.selectedPeriod = period
		},

		// not-yet-converted tables may still contain 80-120% columns;
		// only the 100% one is relevant
		filterToFullUtilization(rvt) {
			const fullUtilizationKey = Object.keys(rvt || {}).find(
				(key) => Number(key.replace('-', '.')) === 1,
			)
			return fullUtilizationKey ? { [fullUtilizationKey]: rvt[fullUtilizationKey] } : rvt
		},

		getTableData(rvt) {
			try {
				const utilizationKeys = Object.keys(rvt)
				const months = [
					...new Set(
						utilizationKeys.flatMap((utilization) =>
							Object.keys(rvt[utilization]).map(Number),
						),
					),
				].sort((a, b) => a - b)

				return months.map((month) => {
					const row = { months: month }
					utilizationKeys.forEach((utilization) => {
						row[`${Math.round(Number(utilization.replace('-', '.')) * 100)}%`] =
							roundHalfUp(Number(rvt[utilization][month] ?? 0), RV_CURRENCY_DIGITS)
					})
					return row
				})
			} catch (error) {
				console.error(
					'Error processing RVT data in getTableData (ResidualValuesTable component):',
					error,
				)
				return []
			}
		},

		onCellEditComplete(event) {
			const { data, newValue, field } = event
			const rowIndex = this.table.indexOf(data)
			const key = `${data.months}-${field}`

			const value = Number(newValue)

			if (this.cellValidationErrors[key]) {
				delete this.cellValidationErrors[key]
				this.cellValidationErrors = { ...this.cellValidationErrors }
			}

			let hasError = false
			let errorMessage = null

			if (newValue === null || newValue === '' || Number.isNaN(value)) {
				hasError = true
				errorMessage = {
					severity: 'error',
					message: '§ui.residual_value_must_be_positive',
				}
			}

			if (!hasError && value < 0) {
				hasError = true
				errorMessage = {
					severity: 'error',
					message: '§ui.residual_value_must_be_positive',
				}
			}

			if (!hasError && rowIndex === 0 && value > Number(this.value)) {
				hasError = true
				errorMessage = {
					severity: 'error',
					message: '§ui.residual_value_exceeds_unit_value',
				}
			}

			if (!hasError && rowIndex > 0) {
				const previousRowValue = Number(this.table[rowIndex - 1][field])
				if (value > previousRowValue) {
					hasError = true
					errorMessage = {
						severity: 'error',
						message: '§ui.residual_value_exceeds_previous',
					}
				}
			}

			if (!hasError && rowIndex >= 0 && rowIndex < this.table.length - 1) {
				const nextRowValue = Number(this.table[rowIndex + 1][field])
				if (value < nextRowValue) {
					hasError = true
					errorMessage = {
						severity: 'error',
						message: '§ui.residual_value_below_next',
					}
				}
			}

			if (hasError) {
				this.cellValidationErrors = {
					...this.cellValidationErrors,
					[key]: errorMessage,
				}
				event.preventDefault()
				return
			}

			if (this.cellValidationErrors[key]) {
				delete this.cellValidationErrors[key]
				this.cellValidationErrors = { ...this.cellValidationErrors }
			}

			data[field] = newValue
		},

		onCellEditCancel(event) {
			const { data, field } = event
			const key = `${data.months}-${field}`

			if (this.cellValidationErrors[key]) {
				delete this.cellValidationErrors[key]
				this.cellValidationErrors = { ...this.cellValidationErrors }
			}
		},
	},
}
</script>
